import { ArrowUpRightIcon } from '@heroicons/react/16/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { useEffect, useRef, useState } from 'react';
import { Address, erc20Abi, formatUnits, Hex, parseUnits, toHex } from 'viem';
import { useAccount, useBalance, useConfig, useWriteContract } from 'wagmi';
import { abi } from '../../abis/ExchangeGateway';
import { DEFAULT_SUBACCOUNT, ESTIMATED_SEQUENCER_PROCESSING_TIME } from '../../constants';
import { useAsyncSelect } from '../../hooks/useAsyncSelect';
import { useExchangeAddress } from '../../hooks/useDataForSigning';
import { useExplorerLink } from '../../hooks/useExplorerLink';
import { DepositModalContext, useModalState } from '../../hooks/useModalState';
import {
  useActiveSelectedSubaccount,
  usePendingSubaccount,
} from '../../hooks/useSelectedSubaccount';
import { useSubaccounts } from '../../hooks/useSubaccounts';
import { TokenBalance, useSupportedTokenBalances } from '../../hooks/useSupportedTokenBalances';
import { TokenDetails, useTokenDetails } from '../../hooks/useTokenDetails';
import { useTokenPrices } from '../../hooks/useTokenPrices';
import { useTransfers } from '../../hooks/useTransfers';
import { isUserRejectedError, parseRpcError } from '../../lib/errors';
import {
  formatDollar,
  formatDuration,
  formatNumber,
  formatNumberWithDecimals,
} from '../../lib/formatters';
import { Gwei, GWEI_BASE, TokenHelper } from '../../lib/gwei';
import { filterQueryKey } from '../../lib/reactQueryHelpers';
import { showToast } from '../../lib/showToast';
import { decodeSubaccountName, hasRegisteredSubaccount } from '../../lib/subaccountHelpers';
import {
  depositSuccessToastMessage,
  tokenApprovalFailedToastMessage,
  tokenApprovalSuccessToastMessage,
} from '../../lib/toasts';
import { viewTransactionTooltip } from '../../lib/tooltips';
import { Alert } from '../Alert';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { SelectableTokenInput } from '../SelectableTokenInput/SelectableTokenInput';
import { StyledDialogStat, StyledDialogStats } from '../StyledDialog/StyledDialogStats';
import { SubmitTxButton, SubmitTxStatus } from '../SubmitTxButton/SubmitTxButton';
import { Tooltip } from '../Tooltip/Tooltip';
import { DepositModalSkeletonUi } from './DepositModalSkeleton';

const MAXIMUM_DEPOSIT_WAIT_TIME = ESTIMATED_SEQUENCER_PROCESSING_TIME;
const DEPOSIT_WAIT_TIME_TOLERANCE = 1000;

/**
 * Note this component adds support for wrapping native tokens.
 * This will be a short lived feature as the contract will wrap for us.
 * When that's ready we should remove this and use the native token directly.
 */

// Minimal ABI for wrapping native tokens
const wrapAbi = [
  {
    name: 'deposit',
    type: 'function',
    inputs: [],
    outputs: [],
    stateMutability: 'payable',
  },
] as const;

interface PendingDeposit {
  name: string;
  amount: bigint;
  timestamp: number;
  subaccountKey: Address | undefined;
  tokenAddress: string;
}

export interface DepositProps {
  subaccountName?: Hex;
  error?: Error;
  clearError: () => void;
  onDepositClick: (address: Address, amount: bigint) => void;
  collateralBalances?: TokenBalance[];
  transactionStatus?: SubmitTxStatus;
  isWalletConnected: boolean;
  defaultTokenAddress?: Address;
  tokenDetails: TokenDetails[];
  approvalTxHash?: `0x${string}`;
  depositTxHash?: `0x${string}`;
  creatingSubaccount?: boolean;
  isWrapping?: boolean;
  nativeBalance?: bigint;
  onContinue?: () => void;
  onClose: () => void;
}

export const DepositModalUi: React.FC<DepositProps> = ({
  collateralBalances,
  onDepositClick,
  subaccountName = DEFAULT_SUBACCOUNT,
  error,
  clearError,
  transactionStatus,
  isWalletConnected,
  defaultTokenAddress,
  tokenDetails,
  depositTxHash,
  creatingSubaccount,
  isWrapping,
  nativeBalance,
  onContinue,
  onClose,
}) => {
  const formattedBalances = collateralBalances?.map((balance) => {
    if (balance.isWrappedNative && nativeBalance !== undefined) {
      return {
        label: balance.name,
        value: {
          ...balance,
          // Override the balance to show native balance for wrapped tokens
          balanceNativeDec: nativeBalance,
        },
      };
    }
    return {
      label: balance.name,
      value: balance,
    };
  });
  const defaultOption =
    formattedBalances?.find(
      (x) => x.value.address.toLowerCase() === defaultTokenAddress?.toLowerCase()
    )?.value || formattedBalances?.[0]?.value;
  const {
    selectedValue: selectedCollateral,
    setSelectedValue: setSelectedCollateral,
    options,
  } = useAsyncSelect(formattedBalances, defaultOption);
  const [amount, setAmount] = useState<string | undefined>();
  const isOpen = useModalState((state) => state.depositModal.openOrClosed === 'open');
  const balanceNativeDec = selectedCollateral?.value.balanceNativeDec;
  const allowanceNativeDec = selectedCollateral?.value.allowanceNativeDec || 0n;

  const decimals = selectedCollateral?.value.decimals || 18;
  const symbol = selectedCollateral?.value.name;
  const tokenPrices = useTokenPrices(symbol ? [symbol] : []);

  const amountNativeDec = amount ? parseUnits(amount, decimals) : 0n;

  const needsApproval = !amountNativeDec ? true : allowanceNativeDec < amountNativeDec;
  const selectedTokenDetails = tokenDetails?.find(
    (tokenDetail) =>
      tokenDetail.address.toLowerCase() === selectedCollateral?.value.address.toLowerCase()
  );

  const getValidationError = () => {
    if (selectedTokenDetails?.depositEnabled === false) {
      return 'Deposits are currently disabled for this token';
    }

    const isWrappedNative = selectedCollateral?.value.isWrappedNative;
    const effectiveBalance =
      isWrappedNative && nativeBalance !== undefined ? nativeBalance : balanceNativeDec;
    // Always show USDe for wrapped tokens in the UI (even if native is ETH locally)
    const displaySymbol = isWrappedNative ? 'USDe' : selectedTokenDetails?.erc20Symbol;

    // Reserve gas buffer for native tokens (0.1 for safety - can be high on some chains)
    const gasBuffer = isWrappedNative ? parseUnits('1', decimals) : 0n;

    if (
      selectedTokenDetails &&
      effectiveBalance !== undefined &&
      TokenHelper.toGwei(effectiveBalance, decimals) < selectedTokenDetails.minDeposit
    ) {
      // For wrapped native tokens, user needs minDeposit + gas buffer
      const requiredAmount = isWrappedNative
        ? selectedTokenDetails.minDeposit + TokenHelper.toGwei(gasBuffer, decimals)
        : selectedTokenDetails.minDeposit;

      return `Your wallet has insufficient ${displaySymbol} to deposit. Transfer at least ${
        formatNumber(requiredAmount) + ' ' + displaySymbol
      } to your wallet to deposit${isWrappedNative ? ' (including gas)' : ''}.`;
    }

    if (
      amountNativeDec &&
      selectedTokenDetails &&
      selectedTokenDetails.minDeposit &&
      Gwei.toGwei(formatUnits(amountNativeDec, decimals)) < selectedTokenDetails.minDeposit
    ) {
      return `Deposit amount must be at least ${
        selectedTokenDetails.name === 'USD'
          ? formatDollar(selectedTokenDetails.minDeposit)
          : formatNumber(selectedTokenDetails.minDeposit) + ' ' + selectedTokenDetails.name
      }`;
    }

    if (
      effectiveBalance !== undefined &&
      amountNativeDec !== undefined &&
      amountNativeDec + gasBuffer > effectiveBalance &&
      !creatingSubaccount
    ) {
      const availableBalance =
        effectiveBalance > gasBuffer
          ? TokenHelper.toGwei(effectiveBalance - gasBuffer, decimals)
          : Gwei.toGwei('0');
      return `Insufficient ${displaySymbol} balance. Available: ${
        selectedTokenDetails?.name === 'USD'
          ? formatDollar(availableBalance)
          : formatNumberWithDecimals(availableBalance) + ' ' + displaySymbol
      }${isWrappedNative ? ' (1 ' + displaySymbol + ' reserved for gas)' : ''}.`;
    }

    return undefined;
  };
  const validationError = getValidationError();

  const isWrappedNative = selectedCollateral?.value.isWrappedNative;
  const effectiveBalance =
    isWrappedNative && nativeBalance !== undefined
      ? nativeBalance
      : selectedCollateral?.value.balanceNativeDec;

  const balanceInGwei =
    effectiveBalance !== undefined ? TokenHelper.toGwei(effectiveBalance, decimals) : undefined;

  const estimatedTime = ESTIMATED_SEQUENCER_PROCESSING_TIME;

  const getButtonActionText = () => {
    if (!amount) return 'Enter Amount';
    if (error) {
      return 'Try Again';
    }
    if (isWrappedNative) {
      return 'Wrap & Deposit';
    }
    return needsApproval ? 'Approve & Deposit' : 'Deposit';
  };

  const depositExplorerLink = useExplorerLink({ txHash: depositTxHash });

  const hasBalance = effectiveBalance !== undefined && effectiveBalance > 0n;

  const buttonDisabled = Boolean(
    !isWalletConnected ||
      ((validationError || !hasBalance || !selectedCollateral || !amountNativeDec) &&
        transactionStatus !== 'complete') ||
      creatingSubaccount
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!amountNativeDec || !selectedCollateral) return;
    onDepositClick(selectedCollateral.value.address, amountNativeDec);
  };

  return (
    <ResponsiveModal onClose={onClose} open={isOpen} title="Deposit">
      {(collateralBalances && selectedCollateral) || !isWalletConnected ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {subaccountName !== DEFAULT_SUBACCOUNT ? (
            <Alert className="text-contentSecondary">
              Depositing to{' '}
              <span className="text-contentPrimary">{decodeSubaccountName(subaccountName)}</span>{' '}
              subaccount
            </Alert>
          ) : null}
          <SelectableTokenInput
            amount={amount}
            selectedToken={selectedCollateral}
            options={options}
            tokenPrice={symbol ? tokenPrices.data[symbol] : undefined}
            balance={balanceInGwei}
            balanceLabel={isWrappedNative ? 'Balance (USDe)' : 'Balance'}
            hasError={Boolean(validationError || error)}
            autoFocus={true}
            setAmount={(amount) => {
              if (error) clearError();
              setAmount(amount);
            }}
            setSelectedToken={(token) => {
              if (error) clearError();
              setSelectedCollateral(token);
            }}
            setMax={() => {
              if (!balanceInGwei) return;
              // For wrapped native tokens, leave gas buffer
              if (isWrappedNative && effectiveBalance !== undefined) {
                const gasBuffer = parseUnits('1', decimals); // Match the validation gas buffer
                const maxAmount = effectiveBalance > gasBuffer ? effectiveBalance - gasBuffer : 0n;
                // maxAmount is already in native decimals, just format it
                setAmount(formatUnits(maxAmount, decimals));
              } else {
                setAmount(formatUnits(balanceInGwei, GWEI_BASE));
              }
            }}
            disabled={Boolean(transactionStatus) || creatingSubaccount}
          />
          <StyledDialogStats>
            <StyledDialogStat label="Min. Deposit">
              <span
                onClick={() =>
                  selectedTokenDetails
                    ? setAmount(formatUnits(selectedTokenDetails.minDeposit, GWEI_BASE))
                    : undefined
                }
                className="cursor-pointer hover:underline"
              >
                {selectedTokenDetails?.name === 'USD'
                  ? formatDollar(selectedTokenDetails?.minDeposit)
                  : `${formatNumber(selectedTokenDetails?.minDeposit)} ${selectedTokenDetails?.name ?? ''}`}
              </span>
            </StyledDialogStat>
            <StyledDialogStat label="Deposit Time">
              ~{formatDuration(estimatedTime)}
            </StyledDialogStat>
            <StyledDialogStat label="Cost">
              ~
              {selectedTokenDetails?.name === 'USD'
                ? formatDollar(selectedTokenDetails?.depositFee)
                : `${formatNumber(selectedTokenDetails?.depositFee)} ${selectedTokenDetails?.name ?? ''}`}
            </StyledDialogStat>
          </StyledDialogStats>
          <div className="flex flex-col space-y-2">
            {(!transactionStatus && validationError) || error ? (
              <Alert variant="error">{validationError || parseRpcError(error)}</Alert>
            ) : null}

            {!isWalletConnected && !transactionStatus && (
              <Alert variant="warning">Connect wallet before depositing</Alert>
            )}
            {creatingSubaccount && (
              <Alert variant={'info'} loading>
                Your subaccount is being created. It should only take a few seconds...
              </Alert>
            )}
            {transactionStatus === 'complete' && !creatingSubaccount ? (
              <>
                <Alert variant="success">
                  <div className="flex items-center justify-between">
                    <div>
                      {formatNumber(Gwei.toGwei(amount || '0'))}{' '}
                      {selectedCollateral?.value.symbol ?? '-'} has been deposited successfully
                    </div>
                    {depositExplorerLink && depositTxHash && (
                      <Tooltip content={viewTransactionTooltip(depositTxHash)}>
                        <a
                          href={depositExplorerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-contentGreen/10 flex items-center font-medium text-contentGreen"
                        >
                          Tx <ArrowUpRightIcon className="size-4" />
                        </a>
                      </Tooltip>
                    )}
                  </div>
                </Alert>
              </>
            ) : null}
            <SubmitTxButton
              type="submit"
              status={transactionStatus}
              actionText={getButtonActionText()}
              submittingText={isWrapping ? 'Wrapping USDe...' : undefined}
              completeText={creatingSubaccount ? 'Depositing...' : 'Continue'}
              onComplete={onContinue}
              disabled={buttonDisabled}
            />
          </div>
        </form>
      ) : (
        <DepositModalSkeletonUi />
      )}
    </ResponsiveModal>
  );
};

export type DepositModalProps = {
  onDeposit?: () => void;
  context: DepositModalContext;
  defaultTokenAddress?: Address;
};

export const DepositModal: React.FC<DepositModalProps> = ({
  onDeposit,
  defaultTokenAddress,
  context,
}) => {
  const isOpen = useModalState((state) => state.depositModal.openOrClosed === 'open');
  const modalContext = useModalState((state) =>
    'context' in state.depositModal ? state.depositModal.context : undefined
  );
  const setDepositModal = useModalState((state) => state.setDepositModal);
  const queryClient = useQueryClient();
  const config = useConfig();
  const { address: walletAddress } = useAccount();
  const { data: nativeBalanceData } = useBalance({
    address: walletAddress,
  });
  const { writeContractAsync } = useWriteContract();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { value: pendingSubaccount } = usePendingSubaccount();
  const { data: collateralBalances, queryKey: supportedBalancesQueryKey } =
    useSupportedTokenBalances();
  const { data: tokenDetails } = useTokenDetails();

  const { address: exchangeAddress } = useExchangeAddress();
  const subaccountNameWithDefault =
    selectedSubaccount?.name || pendingSubaccount || DEFAULT_SUBACCOUNT;

  const { data: subaccounts } = useSubaccounts(walletAddress);

  const [creatingSubaccount, setCreatingSubaccount] = useState(false);
  const [approvalTxHash, setApprovalTxHash] = useState<`0x${string}`>();
  const [depositTxHash, setDepositTxHash] = useState<`0x${string}`>();
  const [transactionStatus, setTransactionStatus] = useState<SubmitTxStatus>();
  const [isWrapping, setIsWrapping] = useState(false);

  const pendingDeposit = useRef<PendingDeposit | null>(null);
  // If we haven't detected a new deposit by the deadline, continue without
  // waiting.
  const pendingDepositDeadline = pendingDeposit.current
    ? pendingDeposit.current.timestamp + MAXIMUM_DEPOSIT_WAIT_TIME
    : Infinity;

  // The WebSocket subscription will automatically update transfers through useTransferSub
  const { data: transfers } = useTransfers({
    subaccountId: selectedSubaccount?.id,
  });
  const matchingTransfers = transfers?.filter(
    (transfer) =>
      pendingDeposit.current &&
      transfer.amount <= pendingDeposit.current.amount &&
      transfer.tokenAddress === pendingDeposit.current.tokenAddress &&
      transfer.type === 'DEPOSIT'
  );
  const newTransfers = matchingTransfers?.filter(
    (transfer) =>
      pendingDeposit.current &&
      transfer.createdAt >= pendingDeposit.current.timestamp - DEPOSIT_WAIT_TIME_TOLERANCE &&
      transfer.type === 'DEPOSIT'
  );

  // Mutation for depositing tokens
  const depositMutation = useMutation({
    mutationKey: ['deposit', { isOpen }],
    mutationFn: async ({
      exchangeAddress,
      address,
      amount,
      token,
    }: {
      exchangeAddress: Address;
      address: Address;
      amount: bigint;
      token: TokenBalance;
    }): Promise<PendingDeposit> => {
      setTransactionStatus('waiting-signature');

      //  This could be fetched by retrieving the block receipt, but we just
      // need a timestamp to compare against new sequencer transfers.
      const timestamp = matchingTransfers?.[0]?.createdAt ?? Date.now();
      const depositHash = await writeContractAsync({
        address: exchangeAddress,
        functionName: 'deposit',
        args: [subaccountNameWithDefault, address, amount, toHex('ethereal', { size: 32 })],
        abi,
      });

      // Store transaction hash
      setDepositTxHash(depositHash);
      setTransactionStatus('submitting');

      await waitForTransactionReceipt(config, {
        hash: depositHash,
      });

      return {
        name: token.name,
        subaccountKey: selectedSubaccount?.name,
        amount: TokenHelper.toGwei(amount, token.decimals),
        timestamp,
        tokenAddress: address,
      };
    },
    onSuccess: async ({ name, amount, timestamp, subaccountKey, tokenAddress }) => {
      await queryClient.invalidateQueries({
        queryKey: filterQueryKey(supportedBalancesQueryKey),
        refetchType: 'all',
      });
      pendingDeposit.current = { name, amount, timestamp, subaccountKey, tokenAddress };

      if (hasRegisteredSubaccount(subaccounts?.data)) {
        setCreatingSubaccount(true);
      }
    },
    onError: async (error) => {
      // Invalidate in case error was caused by balance/allowance having changed
      await queryClient.invalidateQueries({
        queryKey: filterQueryKey(supportedBalancesQueryKey),
        refetchType: 'all',
      });
      setTransactionStatus(undefined);
      if (isUserRejectedError(error)) {
        return;
      }
      showToast(tokenApprovalFailedToastMessage());
    },
  });

  // Detect subaccount creation
  useEffect(() => {
    if (creatingSubaccount && hasRegisteredSubaccount(subaccounts?.data)) {
      setCreatingSubaccount(false);
    }
  }, [creatingSubaccount, onDeposit, subaccounts]);

  // Detect new transfers
  useEffect(() => {
    if (pendingDeposit.current && (newTransfers?.length || Date.now() >= pendingDepositDeadline)) {
      // Balance will be automatically invalidated by useTransferSub when WebSocket message arrives
      setTransactionStatus('complete');

      showToast(
        depositSuccessToastMessage(
          pendingDeposit.current?.subaccountKey,
          pendingDeposit.current?.name,
          pendingDeposit.current?.amount
        )
      );
      pendingDeposit.current = null;
    }
  }, [
    newTransfers,
    pendingDepositDeadline,
    queryClient,
    selectedSubaccount,
    selectedSubaccount?.id,
  ]);

  // Mutation for wrapping native tokens
  const wrapMutation = useMutation({
    mutationKey: ['wrapNative', { isOpen }],
    mutationFn: async ({ tokenAddress, amount }: { tokenAddress: Address; amount: bigint }) => {
      setIsWrapping(true);
      setTransactionStatus('waiting-signature');

      const wrapHash = await writeContractAsync({
        address: tokenAddress,
        functionName: 'deposit',
        abi: wrapAbi,
        value: amount,
      });

      setTransactionStatus('submitting');
      await waitForTransactionReceipt(config, { hash: wrapHash });

      // Refresh balances to get new wrapped token balance
      await queryClient.invalidateQueries({
        queryKey: filterQueryKey(supportedBalancesQueryKey),
      });

      return { tokenAddress, amount };
    },
    onSuccess: ({ tokenAddress, amount }) => {
      setIsWrapping(false);
      // Continue with standard approve/deposit flow
      const token = collateralBalances?.find((t) => t.address === tokenAddress);
      if (!token || !exchangeAddress) return;

      const amountToApprove = amount - token.allowanceNativeDec;
      if (amountToApprove > 0) {
        approveMutation.mutate({
          exchangeAddress,
          address: tokenAddress,
          amount: amountToApprove,
          token,
        });
      } else {
        depositMutation.mutate({ exchangeAddress, address: tokenAddress, amount, token });
      }
    },
    onError: async (error) => {
      setIsWrapping(false);
      await queryClient.invalidateQueries({
        queryKey: filterQueryKey(supportedBalancesQueryKey),
        refetchType: 'all',
      });
      setTransactionStatus(undefined);
      if (isUserRejectedError(error)) {
        return;
      }
      showToast({
        title: 'Wrap Failed',
        message: 'Failed to wrap native tokens. Please try again.',
        variant: 'error',
      });
    },
  });

  // Mutation for approving tokens
  const approveMutation = useMutation({
    mutationKey: ['approveDeposit', { isOpen }],
    mutationFn: async ({
      exchangeAddress,
      address,
      amount,
      token,
    }: {
      exchangeAddress: Address;
      address: Address;
      amount: bigint;
      token: TokenBalance;
    }) => {
      setTransactionStatus('waiting-signature');

      const approveHash = await writeContractAsync({
        address: address,
        functionName: 'approve',
        args: [exchangeAddress, amount],
        abi: erc20Abi,
      });

      // Store transaction hash
      setApprovalTxHash(approveHash);
      setTransactionStatus('submitting');

      await waitForTransactionReceipt(config, { hash: approveHash });

      return { exchangeAddress, address, amount, token, tokenAddress: address };
    },
    onSuccess: (variables) => {
      showToast(tokenApprovalSuccessToastMessage());
      depositMutation.mutate(variables);
    },
    onError: (error) => {
      setTransactionStatus(undefined);
      if (isUserRejectedError(error)) {
        return;
      }
      showToast(tokenApprovalFailedToastMessage());
    },
  });

  const handleDepositClick = async (address: Address, amount: bigint) => {
    const token = collateralBalances?.find(({ address: tokenAddress }) => tokenAddress === address);
    if (!token) return;
    if (!exchangeAddress) return;

    // Check if this is a wrapped native token that needs wrapping first
    if (token.isWrappedNative) {
      // Ensure we have enough balance including gas
      const gasBuffer = parseUnits('1', token.decimals);
      const nativeBalance = nativeBalanceData?.value || 0n;

      // Safety check - ensure we have enough balance for wrap + gas
      if (nativeBalance < amount + gasBuffer) {
        showToast({
          title: 'Insufficient Balance',
          message: `You need at least ${formatUnits(amount + gasBuffer, token.decimals)} ${token.symbol} (including gas) to wrap ${formatUnits(amount, token.decimals)} ${token.symbol}.`,
          variant: 'error',
        });
        return;
      }

      wrapMutation.mutate({ tokenAddress: address, amount });
      return;
    }

    const amountToApprove = amount - token.allowanceNativeDec;

    // Check if approval is needed
    if (amountToApprove > 0) {
      approveMutation.mutate({ exchangeAddress, address, amount: amountToApprove, token });
    } else {
      // No approval needed, directly deposit
      depositMutation.mutate({ exchangeAddress, address, amount, token });
    }
  };

  const onClose = () => {
    setTransactionStatus(undefined);
    setApprovalTxHash(undefined);
    setDepositTxHash(undefined);
    setIsWrapping(false);
    setDepositModal({ openOrClosed: 'closed' });
  };

  const handleContinue = () => {
    onClose();
    if (onDeposit) {
      onDeposit();
    }
  };

  // Only render if the modal is open and the context matches
  if (!isOpen || modalContext !== context) {
    return null;
  }

  return (
    <DepositModalUi
      clearError={() => {
        wrapMutation.reset();
        approveMutation.reset();
        depositMutation.reset();
      }}
      error={wrapMutation.error ?? approveMutation.error ?? depositMutation.error ?? undefined}
      subaccountName={subaccountNameWithDefault}
      collateralBalances={collateralBalances}
      onDepositClick={handleDepositClick}
      transactionStatus={transactionStatus}
      isWalletConnected={Boolean(walletAddress)}
      defaultTokenAddress={defaultTokenAddress}
      tokenDetails={tokenDetails ?? []}
      creatingSubaccount={
        subaccounts !== undefined && subaccounts.data.length === 0 && creatingSubaccount
      }
      isWrapping={isWrapping}
      nativeBalance={nativeBalanceData?.value}
      approvalTxHash={approvalTxHash}
      depositTxHash={depositTxHash}
      onContinue={handleContinue}
      onClose={onClose}
    />
  );
};
